import { PermissionCode } from '@gb-ticket/shared-types';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiRequestError } from '@/lib/api';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { adminTableFrameClassName } from '@/platform/features/admin-panel';
import { MasterDataListState } from '@/platform/features/master-data/master-data-list-state';
import {
  useCreateUnit,
  useCreateUnitConversion,
  useUnitConversions,
  useUnits,
} from '@/platform/features/master-data/use-master-data';

export function UnitsPage() {
  const me = useMe();
  const canWrite = hasPermission(me.data, PermissionCode.MASTER_DATA_MANAGE);
  const units = useUnits();
  const conversions = useUnitConversions();
  const nameById = new Map(units.data?.items.map((item) => [item.id, item.code]));

  return (
    <section className="space-y-8">
      <PlatformPageHeader
        title="Mengeneinheiten"
        description="Stück ist der Anfangsdatensatz C62. Eine Umrechnung ersetzt keine Tankmenge."
      />
      <MasterDataListState
        pending={units.isPending}
        pendingLabel="Mengeneinheiten werden geladen …"
        error={units.isError ? units.error : null}
        errorLabel="Die Mengeneinheiten konnten nicht geladen werden."
        empty={units.data?.items.length === 0}
        emptyLabel="Noch keine Mengeneinheit."
      >
        <div className={adminTableFrameClassName}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Bezeichnung</TableHead>
                <TableHead>Dimension</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {units.data?.items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-mono text-sm">{item.code}</TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.dimension}</TableCell>
                  <TableCell>
                    {item.isActive ? (
                      <Badge variant="outline">Aktiv</Badge>
                    ) : (
                      <Badge variant="secondary">inaktiv</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </MasterDataListState>
      {canWrite ? <UnitForm /> : null}
      <MasterDataListState
        pending={conversions.isPending}
        pendingLabel="Umrechnungen werden geladen …"
        error={conversions.isError ? conversions.error : null}
        errorLabel="Die Umrechnungen konnten nicht geladen werden."
        empty={conversions.data?.items.length === 0}
        emptyLabel="Noch keine Umrechnung."
      >
        <ul className="space-y-1 text-sm">
          {conversions.data?.items.map((item) => (
            <li key={item.id}>
              {nameById.get(item.fromUnitId) ?? item.fromUnitId} →{' '}
              {nameById.get(item.toUnitId) ?? item.toUnitId}: {item.numerator}/{item.denominator}
              {` · ab ${item.validFrom}`}
              {item.validTo ? ` bis ${item.validTo}` : ' · offen'}
            </li>
          ))}
        </ul>
      </MasterDataListState>
      {canWrite ? <ConversionForm units={units.data?.items ?? []} /> : null}
    </section>
  );
}

function UnitForm() {
  const create = useCreateUnit();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [dimension, setDimension] = useState<'count' | 'length' | 'mass' | 'volume' | 'time'>(
    'count',
  );

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    try {
      await create.mutateAsync({ code, name, dimension });
      toast.success('Mengeneinheit angelegt.');
      setCode('');
      setName('');
      setDimension('count');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Mengeneinheit konnte nicht angelegt werden.',
      );
    }
  }

  return (
    <form className="max-w-xl space-y-4" onSubmit={(event) => void onSubmit(event)} noValidate>
      <div className="space-y-2">
        <Label htmlFor="unit-code">Code</Label>
        <Input id="unit-code" value={code} onChange={(event) => setCode(event.target.value)} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="unit-name">Bezeichnung</Label>
        <Input id="unit-name" value={name} onChange={(event) => setName(event.target.value)} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="unit-dimension">Dimension</Label>
        <Select
          value={dimension}
          onValueChange={(value) =>
            setDimension(value as 'count' | 'length' | 'mass' | 'volume' | 'time')
          }
        >
          <SelectTrigger id="unit-dimension" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="count">Anzahl</SelectItem>
            <SelectItem value="length">Länge</SelectItem>
            <SelectItem value="mass">Masse</SelectItem>
            <SelectItem value="volume">Volumen</SelectItem>
            <SelectItem value="time">Zeit</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button type="submit" disabled={create.isPending}>
        Einheit anlegen
      </Button>
    </form>
  );
}

function ConversionForm({ units }: { units: { id: string; code: string; isActive: boolean }[] }) {
  const create = useCreateUnitConversion();
  const active = units.filter((item) => item.isActive);
  const [fromUnitId, setFromUnitId] = useState(active[0]?.id ?? '');
  const [toUnitId, setToUnitId] = useState(active[1]?.id ?? active[0]?.id ?? '');
  const [numerator, setNumerator] = useState('1');
  const [denominator, setDenominator] = useState('1');
  const [validFrom, setValidFrom] = useState('');

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    try {
      await create.mutateAsync({
        fromUnitId,
        toUnitId,
        numerator: Number(numerator),
        denominator: Number(denominator),
        validFrom,
      });
      toast.success('Umrechnung angelegt.');
      setNumerator('1');
      setDenominator('1');
      setValidFrom('');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Umrechnung konnte nicht angelegt werden.',
      );
    }
  }

  return (
    <form className="max-w-xl space-y-4" onSubmit={(event) => void onSubmit(event)} noValidate>
      <div className="space-y-2">
        <Label htmlFor="conversion-from">Von</Label>
        <Select value={fromUnitId || undefined} onValueChange={setFromUnitId}>
          <SelectTrigger id="conversion-from" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {active.map((item) => (
              <SelectItem key={item.id} value={item.id}>
                {item.code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="conversion-to">Nach</Label>
        <Select value={toUnitId || undefined} onValueChange={setToUnitId}>
          <SelectTrigger id="conversion-to" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {active.map((item) => (
              <SelectItem key={item.id} value={item.id}>
                {item.code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="conversion-numerator">Zähler</Label>
        <Input
          id="conversion-numerator"
          type="number"
          min={1}
          value={numerator}
          onChange={(event) => setNumerator(event.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="conversion-denominator">Nenner</Label>
        <Input
          id="conversion-denominator"
          type="number"
          min={1}
          value={denominator}
          onChange={(event) => setDenominator(event.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="conversion-from-date">Gültig ab</Label>
        <Input
          id="conversion-from-date"
          placeholder="JJJJ-MM-TT"
          value={validFrom}
          onChange={(event) => setValidFrom(event.target.value)}
        />
      </div>
      <Button type="submit" variant="outline" disabled={create.isPending || active.length < 2}>
        Umrechnung anlegen
      </Button>
    </form>
  );
}
